import { TFunction } from '@mint-lib/i18n';
import { isSameDay } from 'date-fns';
import z from 'zod/v4';

import {
  EntityTypesEnum,
  FlowStatusesEnum,
  FlowTypesEnum,
  OperationTypesEnum,
  PENDING_REQUEST_NOTIFICATION,
} from '../constants/singleFlow.js';
import { isStringAListOfEmails } from '../utils/singleFlow.js';
import { ApproverEntitySchema, OptionItem, OptionSchema } from './shared.js';

const getApproverItemSchema = (t: TFunction) => {
  return z.object({
    entityId: z
      .string()
      .nullable()
      .refine((val) => val !== null, {
        error: t('Field is required'),
      }),
    entityLabel: z
      .string()
      .nullable()
      .refine((val) => val !== null, {
        error: t('Field is required'),
      }),
    type: EntityTypesEnum.nullable().refine((val) => val !== null, {
      error: t('Field is required'),
    }),
    operation: OperationTypesEnum.nullable(),
  });
};

const getApprovedRequestNotificationSchema = (t: TFunction) => {
  return z.discriminatedUnion('enabled', [
    z.object({
      enabled: z.literal(false),
      recipientEmails: z
        .string()
        .refine(isStringAListOfEmails, t('Incorrect email address')),
    }),
    z.object({
      enabled: z.literal(true),
      recipientEmails: z
        .string()
        .min(1, t('At least one email is required'))
        .refine(isStringAListOfEmails, t('Incorrect email address')),
    }),
  ]);
};

const dateRangeShape = {
  dateFrom: z.date(),
  dateTo: z.date().nullable(),
};

const validateDateRange = (
  data: { dateFrom: Date; dateTo: Date | null },
  ctx: z.RefinementCtx,
  t: TFunction,
) => {
  const { dateFrom, dateTo } = data;

  if (!dateTo || isSameDay(dateFrom, dateTo)) {
    return;
  }

  if (dateFrom > dateTo) {
    ctx.addIssue({
      code: 'custom',
      path: ['dateFrom'],
      message: t('Start date cannot be set after End date'),
    });

    ctx.addIssue({
      code: 'custom',
      path: ['dateTo'],
      message: t('End date cannot be set before Start date'),
    });
  }
};

export const getFlowFormSchema = (t: TFunction) => {
  return z
    .object({
      name: z
        .string()
        .min(2, t('Minimum number of symbols: {{number}}', { number: 2 })),
      type: FlowTypesEnum,
      requesterType: EntityTypesEnum.nullable().refine((val) => val !== null, {
        error: t('Field is required'),
      }),
      requesterValue: z
        .array(OptionSchema)
        .min(1, { error: 'Add one requester at least' }),
      approvers: z.array(getApproverItemSchema(t)),
      pendingRequestNotification: z.object({
        enabled: z.boolean(),
        frequencyDays: z
          .number({ error: t('Enter valid number') })
          .int(t('Value cannot contain decimals'))
          .min(
            PENDING_REQUEST_NOTIFICATION.MIN_FREQUENCY_DAYS,
            t('Too small number. Minimum is {{count}} day', {
              count: PENDING_REQUEST_NOTIFICATION.MIN_FREQUENCY_DAYS,
            }),
          )
          .max(
            PENDING_REQUEST_NOTIFICATION.MAX_FREQUENCY_DAYS,
            t('Too large number. Maximum is {{count}} days', {
              count: PENDING_REQUEST_NOTIFICATION.MAX_FREQUENCY_DAYS,
            }),
          ),
      }),
      approvedRequestNotification: getApprovedRequestNotificationSchema(t),
      ...dateRangeShape,
    })
    .superRefine((data, ctx) => {
      validateDateRange(data, ctx, t);
    });
};
export type ApproverItemOutput = z.output<
  ReturnType<typeof getApproverItemSchema>
>;
export type ApproverItemInput = z.input<
  ReturnType<typeof getApproverItemSchema>
>;
export type FlowFormOutput = z.output<ReturnType<typeof getFlowFormSchema>>;
export type FlowFormInput = z.input<ReturnType<typeof getFlowFormSchema>>;

export type EntityGroupType = OptionItem[];
export type LogicalGroupType = EntityGroupType[];
export type ApprovalSequenceType = LogicalGroupType[];
export type TabContentProps = {
  isFlowInUse: boolean;
};

const FlowItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  flowType: FlowTypesEnum,
  status: FlowStatusesEnum,
  inUse: z.boolean(),
  createdBy: z.string(),
  createdDate: z.string(),
  lastEditedBy: z.string(),
  lastEditedAt: z.string(),
  requesters: z.array(ApproverEntitySchema),
  approvers: z.array(ApproverEntitySchema),
  pendingRequestNotification: z.object({
    enabled: z.boolean(),
    frequencyDays: z.number(),
  }),
  approvedRequestNotification: z.object({
    enabled: z.boolean(),
    recipientEmails: z.array(z.email()),
  }),
  dateFrom: z.string(),
  dateTo: z.string().nullable(),
});

export type FlowItem = z.infer<typeof FlowItemSchema>;
